import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { Theme } from '@material-ui/core';

import { useGetByggQuery, useGetByggListQuery } from '@klp/bygg';
import type { Bygg } from '@klp/bygg/classes';
import { BuildingTabs } from '@klp/bygg/classes';
import { ByggRoutes } from '@klp/bygg/config';
import { ClientRoutes } from '@klp/client/config';
import { Conditional } from '@klp/conditional';
import { AppRoutes } from '@klp/core/app/config';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Link, Typography } from '@klp/design';
import { Amount } from '@klp/design/Amount';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Loader } from '@klp/design/Loader';
import type { ITableConsumerState } from '@klp/design/Table';
import { Table, TableColumnDef, TableFilterDef } from '@klp/design/Table';
import { useKlpStyles } from '@klp/design/Theme';
import { ErrorNotification } from '@klp/errorNotification';
import { FileDownload } from '@klp/filedownload';
import { isPdf } from '@klp/helpers';

import { fakturaApiRoutes } from '../api';
import { FakturaStatus } from '../classes';
import type { Faktura } from '../classes';
import { useGetFakturaListQuery } from '../fakturaApi';

const styles = (theme: Theme) => ({
    center: {
        textAlign: 'center',
    },
    download: {
        textAlign: 'center',
        '& > *': {
            display: 'inline-block',
            '& > *': {
                margin: theme.spacing(1),
            },
        },
    },
    previewDownload: {
        cursor: 'pointer',
    },
});

interface IProps {
    isByggFakturaView?: boolean;
}

export const FakturaList: React.FC<IProps> = ({ isByggFakturaView }) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const params = useAppParams();

    const { selectedClient, byggNummer } = params;
    const kundeNummer = selectedClient?.kundeNummer;

    const {
        data: fakturaData,
        isLoading: fakturaListLoading,
        error: fakturaListError,
    } = useGetFakturaListQuery(
        { byggNummer, kundeNummer: kundeNummer! },
        { skip: !kundeNummer },
    );

    const fakturaList = fakturaData?.fakturaList || [];

    const { data: singleBygg } = useGetByggQuery(
        { byggNummer: byggNummer! },
        { skip: !byggNummer },
    );

    const { data: byggListData } = useGetByggListQuery(
        { kundeNummer: kundeNummer! },
        { skip: !kundeNummer || !!byggNummer },
    );

    const byggList = byggListData || [];
    const bygg = singleBygg;

    const renderByggName = (byggNummerParam: string): JSX.Element => {
        let selectedBygg = bygg;
        if (!selectedBygg) {
            selectedBygg = byggList.find(
                (byggRecord: Bygg) => byggRecord.byggNummer === byggNummerParam,
            );
        }

        if (selectedBygg?.byggNummer) {
            return (
                <Link
                    href={ByggRoutes.single(
                        selectedBygg.byggNummer,
                        BuildingTabs.invoices,
                    )}
                    klpColor="main"
                    klpTypographyVariant="small"
                >
                    {selectedBygg.navn}
                </Link>
            );
        }
        return <Fragment>-</Fragment>;
    };

    const renderFakturaDelay = (value: number): JSX.Element =>
        value <= 0 ? (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
        ) : (
            <Typography klpTypographyVariant="small" className={classes.center}>
                {value}
                {t('general.days', { count: value })}
            </Typography>
        );

    const renderFakturaStatus = (value: string): JSX.Element => (
        <Typography klpTypographyVariant="small" className={classes.center}>
            {t(`fakturaStatus.${value}`)}
        </Typography>
    );

    const renderFakturaDownload = <DataType, FilterType>(
        value: string,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: Faktura,
    ): JSX.Element => {
        const { pdfFileName } = row;
        return (
            <div className={classes.download}>
                {pdfFileName && isPdf(pdfFileName) && (
                    <FileDownload
                        path={fakturaApiRoutes.download(value)}
                        name={`${value}.pdf`}
                        preview={true}
                    >
                        <Illustration
                            className={classes.previewDownload}
                            icon={IconName.SEARCH}
                            klpPalette="primary"
                            klpColor="main"
                        />
                    </FileDownload>
                )}

                {pdfFileName && (
                    <FileDownload
                        path={fakturaApiRoutes.download(value)}
                        name={`${value}.pdf`}
                    >
                        <Illustration
                            className={classes.previewDownload}
                            icon={IconName.DOWNLOAD}
                            klpPalette="primary"
                            klpColor="main"
                        />
                    </FileDownload>
                )}
            </div>
        );
    };

    const renderValueWithValuta = <DataType, FilterType>(
        value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: Faktura,
    ): JSX.Element => {
        const { valuta } = row;
        return (
            <>
                <Amount value={value} /> &#8239;{valuta}
            </>
        );
    };

    const tableDef: Array<TableColumnDef<Faktura, any>> = [];

    tableDef.push(
        new TableColumnDef<Faktura, any>({
            translation: 'faktura.fakturaNumber',
            key: 'fakturaNummer',
            type: 'string',
            sortable: true,
        }),
    );

    if (!isByggFakturaView) {
        tableDef.push(
            new TableColumnDef<Faktura, any>({
                translation: 'bygg.bygg',
                key: 'byggNummer',
                render: renderByggName,
                type: 'string',
                sortable: true,
            }),
        );
    }

    tableDef.push(
        new TableColumnDef<Faktura, any>({
            translation: 'faktura.dueDate',
            key: 'forfallsDato',
            type: 'date',
            sortable: true,
        }),
        new TableColumnDef<Faktura, any>({
            translation: 'faktura.delayed',
            key: 'forsinketDager',
            render: renderFakturaDelay,
            type: 'number',
            sortable: true,
        }),
        new TableColumnDef<Faktura, any>({
            translation: 'faktura.lastpaymentdate',
            key: 'sisteInnbetaling',
            type: 'date',
            sortable: true,
        }),
        new TableColumnDef<Faktura, any>({
            translation: 'faktura.amount',
            key: 'sum',
            type: 'number',
            sortable: true,
            align: 'right',
            render: renderValueWithValuta,
        }),
        new TableColumnDef<Faktura, any>({
            translation: 'faktura.left',
            key: 'sumUtestaaende',
            type: 'number',
            sortable: true,
            align: 'right',
            render: renderValueWithValuta,
        }),
        new TableColumnDef<Faktura, any>({
            translation: 'faktura.kid',
            key: 'kid',
            type: 'string',
            sortable: true,
        }),
        new TableColumnDef<Faktura, any>({
            translation: 'faktura.status',
            key: 'statusCode',
            render: renderFakturaStatus,
            type: 'string',
            sortable: true,
        }),
    );

    tableDef.push(
        new TableColumnDef<Faktura, any>({
            translation: 'general.previewDownload',
            key: 'download',
            render: renderFakturaDownload,
        }),
    );

    return (
        <Loader isLoading={fakturaListLoading}>
            <Conditional
                condition={!!selectedClient}
                errorComponent={
                    <ErrorNotification
                        title="clientSelect.errorNoClientsSelected.title"
                        description="clientSelect.errorNoClientsSelected.description"
                        backTarget={AppRoutes.mainPage()}
                        backTitle="general.goBackToFrontpage"
                        mainTarget={ClientRoutes.clientSelect()}
                        mainTitle="clientSelect.chooseClient"
                        returnUrl={true}
                    />
                }
            >
                <Conditional
                    condition={!fakturaListError}
                    errorComponent={
                        <ErrorNotification
                            title="faktura.errorDownload.title"
                            description="faktura.errorDownload.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                        />
                    }
                >
                    <Conditional
                        condition={!fakturaListLoading}
                        errorComponent={
                            <ErrorNotification
                                title="faktura.errorNotFound.title"
                                backTarget={AppRoutes.mainPage()}
                                backTitle="general.goBackToFrontpage"
                            />
                        }
                    >
                        <Table<Faktura, any>
                            id="FakturaList"
                            data={fakturaList}
                            emptyTableMessage="faktura.emptyTable"
                            filter={[
                                new TableFilterDef<FakturaStatus>({
                                    filterKey: 'statusCode',
                                    filterGroup: {
                                        delayed: [FakturaStatus.Overdue],
                                        tobepaid: [
                                            FakturaStatus.AwaitingPayment,
                                            FakturaStatus.PartiallyPayed,
                                        ],
                                        refunds: [
                                            FakturaStatus.AwaitingRefund,
                                            FakturaStatus.Refunded,
                                        ],
                                        paid: [FakturaStatus.Paid],
                                        purring: [FakturaStatus.Purring],
                                        inkasso: [FakturaStatus.Inkasso],
                                    },
                                    translations: {
                                        delayed: 'faktura.delayedPayments',
                                        tobepaid: 'faktura.incomingPayments',
                                        refunds: 'faktura.refunds',
                                        paid: 'faktura.settledInvoices',
                                        purring: 'faktura.purring',
                                        inkasso: 'faktura.inkasso',
                                    },
                                }),
                            ]}
                            activeFilter={['tobepaid', 'delayed']}
                            columns={
                                tableDef as Array<TableColumnDef<Faktura, any>>
                            }
                        />
                    </Conditional>
                </Conditional>
            </Conditional>
        </Loader>
    );
};

FakturaList.displayName = 'FakturaList';
